import { DocumentNode, useMutation } from "@apollo/client";
import { Form, Formik } from "formik";
import { useCallback, useMemo } from "react";
import styled from "styled-components";

import {
  Button,
  Card,
  FieldWrapper,
  Fieldset,
  Input,
  Spacing,
  Tipbox,
} from "@otta/design";

type Item = Record<string, unknown> & {
  id: string;
};

const Row = styled.div`
  justify-content: flex-end;
  display: flex;
  gap: 1rem;
`;

/**
 * This evil component summons a nasty form for your graphql object from the ether
 * Just pretend you didn't see this, and please don't imitate it my goodness
 */
export function EditForm<A extends Item>({
  mutation,
  omitFields,
  numberFields,
  onClose,
  title,
  item,
}: {
  item: A;
  title?: string;
  mutation: DocumentNode;
  onClose?: () => void;
  numberFields?: (keyof A)[];
  omitFields?: (keyof A)[];
}): React.ReactElement {
  const [update, { error, loading, data }] = useMutation(mutation);
  const numbers = numberFields ?? [];

  const omitKeys = useMemo(
    () => ["id", "__typename", ...(omitFields ?? [])] as (keyof A)[],
    [omitFields]
  );

  const onSubmit = useCallback(
    (id: string, input: A) => {
      const filtered = Object.fromEntries(
        Object.entries(input).filter(([k]) => !omitKeys.includes(k))
      );
      update({ variables: { id, input: filtered as A } });
    },
    [update, omitKeys]
  );

  const fields: (keyof A)[] = useMemo(
    () => Object.keys(item).filter(k => !omitKeys.includes(k)),
    [item, omitKeys]
  );

  return (
    <Card>
      <Spacing size={1}>
        {error && <Tipbox level="error">{error.message}</Tipbox>}
        {data && <Tipbox level="positive">Changes saved</Tipbox>}

        <Formik<A>
          onSubmit={values => onSubmit(item.id, values)}
          initialValues={item}
        >
          {({ handleChange, values }) => (
            <Form>
              <Spacing size={1}>
                <Fieldset legend={title}>
                  {fields.map(name => (
                    <FieldWrapper
                      label={String(name)}
                      fieldError={undefined}
                      required
                    >
                      {({ field }) => (
                        <Input
                          name={name as string}
                          onChange={handleChange}
                          type={numbers.includes(name) ? "number" : "text"}
                          value={(values[name] as string) ?? ""}
                          {...field}
                        />
                      )}
                    </FieldWrapper>
                  ))}
                </Fieldset>
                <Row>
                  <Button type="submit" level="primary" disabled={loading}>
                    Save
                  </Button>
                  {!!onClose && (
                    <Button
                      type="button"
                      level="secondary"
                      onClick={() => onClose()}
                    >
                      Close
                    </Button>
                  )}
                </Row>
              </Spacing>
            </Form>
          )}
        </Formik>
      </Spacing>
    </Card>
  );
}
